import { createSelector } from '@reduxjs/toolkit';
import moment from 'moment';

// Selectors for state slices
export const selectEvents = (state) => state.eventState.events;
export const selectPlans = (state) => state.plannedItemState.plans;
export const selectColorCodes = (state) => state.plannedItemState.colorCodes;
export const selectWinterDuties = (state) => state.winterdutyState?.winterDuties;
export const selectPersons = (state) => state.personState.persons || [];

// Selector for unanswered events for a person
export const selectUnansweredEventsForPerson = createSelector(
    [selectEvents, (_, personId) => personId],
    (events, personId) =>
        events.filter((event) =>
            event.invitations.some((invitation) => invitation.status === 0 && invitation.personId === personId)
        )
);

// Selector for items today for a person
export const selectItemsTodayForPerson = createSelector(
    [selectPlans, (_, personId) => personId],
    (plans, personId) => {
        const date = moment();
        return plans.filter(
            (plan) =>
                plan.personId === personId &&
                date.isSameOrAfter(moment(plan.startTime), 'day') &&
                date.isSameOrBefore(moment(plan.endTime))
        );
    }
);

// Selector for the current item for a person
export const selectCurrentItemForPerson = createSelector(
    [selectItemsTodayForPerson, (_, personId) => personId],
    (itemsToday, personId) => {
        if (itemsToday.length > 0) {
            const date = moment();
            return itemsToday.find(
                (item) => date.isAfter(moment(item.startTime)) && date.isBefore(moment(item.endTime))
            );
        }
    }
);


// Selects all items for all persons as an object: { [personId]: item }
export const selectAllCurrentItemsByPersonId = createSelector(
    [selectPersons, state => state],
    (persons, state) => {
        const result = {};
        Object.values(persons || {}).forEach(person => {
            result[person.id] = selectCurrentItemForPerson(state, person.id);
        });
        return result;
    }
);

// Selector for checking if a person is on winter duty
export const selectIsWinterDutyForPerson = createSelector(
    [selectWinterDuties, (_, personId) => personId],
    (winterDuties, personId) =>
        Array.isArray(winterDuties) && winterDuties.length > 0
            ? !!winterDuties.find((duty) => duty.personId === personId)
            : false
);

// Selector for finding a specific color code by system name
export const selectColorCodeBySystemName = createSelector(
    [selectColorCodes, (_, systemName) => systemName],
    (colorCodes, systemName) => 
        Array.isArray(colorCodes) && colorCodes.length > 0
             ? colorCodes?.find((colorCode) => colorCode.systemName === systemName)
             : false
);